import React, { FC } from 'react';
import classNames from 'classnames';
import { camelToSnake } from '../../../../caseUtils';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import {
  OpenCommentaryCard,
  PaperCommentaryCard,
  HybridCommentaryCard,
} from '../../../../commentary/card';
import {
  Commentary,
  CommentaryTypeLabel,
} from '../../../../commentary/commentary.types';
import { EntityCommentary } from '../../../search.types';
import { HitCommentariesListSeeMore } from './HitCommentariesListSeeMore';
import styles from './hit_commentaries_list.module.scss';
import { BackgroundBox } from '../../../../components/background-box/BackgroundBox';

type Props = {
  commentaries: Array<EntityCommentary>;
  docId: string;
  numberOfComments: number;
  nextCommentAuthors: string[];
  otherCommentAuthorsCount: number;
  source: string;
  searchType: string;
  allCommentariesGetter: (
    docId: string,
    query: string,
  ) => Promise<{ data: Array<Commentary> }>;
};

export const HitCommentariesList: FC<Props> = ({
  commentaries,
  numberOfComments,
  otherCommentAuthorsCount,
  nextCommentAuthors = [],
  source,
  docId,
  allCommentariesGetter,
  searchType,
}) => {
  const onCommentaryClicked = (commentary: EntityCommentary) => () => {
    doctrineAnalytics('Search AbstractCommentClicked', {
      ...camelToSnake(commentary),
      reportOriginalQueryKey: true,
      search_type: searchType,
    });
  };

  const extraCommentaries = numberOfComments - commentaries.length;
  const hasMore = extraCommentaries > 0;
  const hasCommentaries = numberOfComments > 0;

  if (!hasCommentaries) {
    return null;
  }

  return (
    <BackgroundBox className={styles.container}>
      <ul className={classNames(hasMore && styles.list)}>
        {commentaries.map((commentary) => {
          if (commentary.typeLabel === CommentaryTypeLabel.HYBRID) {
            return (
              <HybridCommentaryCard
                className={styles.item}
                commentary={commentary}
                key={commentary.commentaryId}
                source={source}
                onCommentaryClicked={onCommentaryClicked(commentary)}
              />
            );
          }
          if (commentary.typeLabel === CommentaryTypeLabel.PAPER) {
            return (
              <PaperCommentaryCard
                className={styles.item}
                key={commentary.commentaryId}
                commentary={commentary}
              />
            );
          }
          return (
            <OpenCommentaryCard
              className={styles.item}
              key={commentary.commentaryId}
              commentary={commentary}
              source={source}
              onCommentaryClicked={onCommentaryClicked(commentary)}
            />
          );
        })}
      </ul>
      {hasMore && (
        <HitCommentariesListSeeMore
          docId={docId}
          allCommentariesGetter={allCommentariesGetter}
          searchType={searchType}
          numberOfComments={numberOfComments}
          nextCommentAuthors={nextCommentAuthors}
          otherCommentAuthorsCount={otherCommentAuthorsCount}
        />
      )}
    </BackgroundBox>
  );
};
